import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  IconButton,
  Alert,
} from "@mui/material";
import {
  DataGridPremium,
  GridColDef,
  GridRowSelectionModel,
  GridToolbar,
} from "@mui/x-data-grid-premium";
import { SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import {
  fetchRelatedProducts,
  createXevmpd,
  fetchXevmpdList,
  fetchRegistrationPacks,
  fetchExistingPackSelections,
} from "../../api/apiFunctions";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/Loading";
import { XevmpdContext } from "../../App";
import { useSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";
import NoRecordsFound from "../../components/NoRecordsFound";
import { Description } from "@mui/icons-material";

interface XevmpdPackageProps {
  open: boolean;
  onClose: () => void;
  regdata: any[];
  sideMenuData: any;
  eleigibleRegrecords:any[]
}

const packRows = Array.from({ length: 50 }, (_, index) => ({
  id: index + 1,
  name: `Pack-${index + 1}`,
  type: index % 2 === 0 ? "Box" : "Container",
  size: `${index + 1 * 10}`,
}));

// UAT #31 (23 Sep 2026): Full Name (Registration "Name Parts" of the row language) and EV Code (Pack Presentation
// xEVMPDCode) let the user tell the presentations apart before initiating.
// UAT #34 reopened (28 Sep 2026): minimum widths so the columns are not squeezed into the 800 px dialog; the grid
// scrolls horizontally instead (class add-wizard-grid keeps the scroll bar visible, see index.scss).
export const packColumns: GridColDef[] = [
  { field: "packageName", headerName: "Package Name", flex: 1, minWidth: 200 },
  { field: "fullName", headerName: "Full Name", flex: 1, minWidth: 260 },
  { field: "presentationLanguage", headerName: "Presentation Language", flex: 0.5, minWidth: 170 },
  { field: "xEVMPDCode", headerName: "EV Code", flex: 0.5, minWidth: 130 },
  // UAT Kenvue #63 (26 Oct 2026): Pack Presentation › Previous EVMPD Code (it flows into the record, UAT #61)
  { field: "previousEVMPDCode", headerName: "Previous EV Code", flex: 0.5, minWidth: 150 },
  { field: "packageDescription", headerName: "Package Description", flex: 0.3, minWidth: 200 },
  // UAT #43 (29 Sep 2026): no "Existing xEVMPD" column; covered packs stay greyed out and are named below the grid
];

/** "Dutch, German" / "Dutch;German" → ["Dutch", "German"]. */
const splitLanguages = (raw: any): string[] =>
  String(raw ?? "")
    .split(/[,;/]/)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * UAT #40 follow-up (28 Sep 2026) — text of a Pack Presentation "Full Name" value. On K1SBX the CM field is a select
 * whose value is a list of { id, name } nodes (concept IMTTNWHWFullName); plain text and a single node are accepted too.
 */
export const packFullNameOf = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.length ? packFullNameOf(value[0]) : "";
  if (typeof value === "object") {
    for (const key of ["name", "label", "fullName", "value"]) {
      const text = String(value?.[key] ?? "").trim();
      if (text) return text;
    }
    return "";
  }
  return String(value).trim();
};

/**
 * UAT #52 (8 Oct 2026) — major version of a registration from its version label ("V7.0" → 7). The Add wizard only lists
 * registrations at a major version; their Pack Presentation is read from that version, not from a later minor one.
 */
export const registrationMajorVersionOf = (registration: any): number | undefined => {
  const major = Number(/^V(\d+)\./.exec(String(registration?.regVersion ?? ""))?.[1]);
  return major > 0 ? major : undefined;
};

/**
 * UAT #9 (re-opened 7 Sep 2026) — Registration "Pack Presentation" entries can carry several languages; each xEVMPD
 * record covers exactly one, so the dialog lists one selectable row per (pack, language). `rowId` is only the grid
 * key; `id` stays the real Pack Presentation id that is stored in PackageData.
 */
export const expandPackPresentationRows = (dynamicData: any[] | undefined) => {
  if (!Array.isArray(dynamicData)) return [];
  return dynamicData.flatMap((item: any) => {
    const packageName = Array.isArray(item?.packages)
      ? item.packages.map((pkg: any) => pkg?.name).filter(Boolean).join(", ")
      : item?.packages?.name ?? "";
    const languages = (Array.isArray(item?.presentationLanguage) ? item.presentationLanguage : [])
      .map((lang: any) => String(lang?.name ?? "").trim())
      .filter(Boolean);
    return (languages.length ? languages : [""]).map((language: string) => ({
      rowId: `${item?.id}__${language}`,
      id: item?.id,
      packageName,
      presentationLanguage: language,
      packageDescription: item?.packageDescription ?? "",
      xEVMPDCode: String(item?.xEVMPDCode ?? "").trim(), // UAT #31: the pack EV code kept in Registration (UAT #26)
      previousEVMPDCode: String(item?.previousEVMPDCode ?? "").trim(), // UAT #63: the code of the pack it replaces
      // UAT #40: the pack's own Full Name (Registration "Pack Presentation", a select value) — it selects the Name Parts row
      ...(packFullNameOf(item?.fullName) ? { fullName: packFullNameOf(item?.fullName) } : {}),
    }));
  });
};

/**
 * UAT #31 / #40 (reopened 30 Sep 2026) — the dialog's Full Name column shows the Full Name the record will use: the
 * pack's own Full Name when a Name Parts row of its language carries the same Full Name (same selection as
 * selectPresentationDetailsForPack), blank otherwise — blank on the pack, on Name Parts, on both, or no such row.
 * Display-only: PackageData keeps the pack's own Full Name from the dialog data.
 */
export const attachFullNames = (rows: any[], nameParts: any[] | undefined) => {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) => {
    const picked = selectPresentationDetailsForPack(Array.isArray(nameParts) ? nameParts : [], row) ?? [];
    return { ...row, fullName: packFullNameOf(picked[0]?.fullName) };
  });
};

/** Marks rows whose (pack id, language) already has a live xEVMPD record; returns the same rows, flagged. */
export const flagExistingPackSelections = (rows: any[], existing: any[] | undefined) => {
  if (!Array.isArray(rows) || !Array.isArray(existing) || existing.length === 0) return rows;
  return rows.map((row) => {
    const match = existing.find((e: any) => {
      if (String(e?.packId ?? "") !== String(row?.id ?? "")) return false;
      const storedLanguage = String(e?.presentationLanguage ?? "").trim().toLowerCase();
      const rowLanguage = String(row?.presentationLanguage ?? "").trim().toLowerCase();
      return !storedLanguage || !rowLanguage || storedLanguage === rowLanguage;
    });
    return match ? { ...row, disabled: true, existingRecordID: match.recordID ?? "" } : row;
  });
};

/** One Presentation Details row per pack language with blank name parts (Full Name ""), stamped with the pack. */
const blankPresentationDetails = (packId: string, packLanguages: string[], packDescription: string) =>
  (packLanguages.length ? packLanguages : [""]).map((language) => ({
    id: packId,
    fullName: "",
    presentationLanguage: language,
    language,
    description: packDescription,
  }));

/**
 * UAT #9 — the Registration "Name Parts" rows (one per language) are reduced to the selected pack: by id, else by
 * language (comma-separated allowed), else rows without any language (stamped with the pack language). When a pack is
 * selected and nothing matches, exactly one row per pack language is synthesised with blank name parts — never every
 * language of the registration (re-opened 7 Sep 2026: xEVMPD-10535651 / -10535465). No pack → all rows are kept.
 * UAT #40 (reopened 30 Sep 2026): of those rows only the ones whose Full Name equals the pack's own Full Name are taken;
 * a blank Full Name on the pack, on the rows or on both, or no row with that Full Name, gives the same blank rows.
 */
export const selectPresentationDetailsForPack = (rows: any[] | undefined, pack: any) => {
  if (!Array.isArray(rows)) return rows;
  const packId = String(pack?.id ?? "");
  const packLanguages = splitLanguages(pack?.presentationLanguage);
  const packDescription = pack?.packageDescription ?? "";
  const packSelected = Boolean(packId) || packLanguages.length > 0;
  const rowLanguage = (r: any) => String(r?.presentationLanguage || r?.language || "");
  let selected = packId ? rows.filter((r) => String(r?.id ?? "") === packId) : [];
  if (selected.length === 0 && packLanguages.length > 0) {
    const wanted = packLanguages.map((l) => l.toLowerCase());
    selected = rows.filter((r) => wanted.includes(rowLanguage(r).trim().toLowerCase()));
  }
  if (selected.length === 0 && packSelected) {
    selected = rows.filter((r) => !rowLanguage(r).trim());
    if (selected.length === 0) return blankPresentationDetails(packId, packLanguages, packDescription);
  }
  if (selected.length === 0) selected = rows;
  // UAT #40 (reopened 30 Sep 2026): the pack's own Full Name picks the Name Parts row; a blank Full Name on either
  // side, or no row with that Full Name, leaves Presentation Details blank.
  if (packSelected) {
    const packFullName = packFullNameOf(pack?.fullName).toLowerCase();
    const byFullName = packFullName
      ? selected.filter((r) => packFullNameOf(r?.fullName).toLowerCase() === packFullName)
      : [];
    if (byFullName.length === 0) return blankPresentationDetails(packId, packLanguages, packDescription);
    selected = byFullName;
  }
  const defaultLanguage = packLanguages[0] ?? "";
  return selected.map((item: any) => {
    const language = rowLanguage(item).trim() ? rowLanguage(item) : defaultLanguage;
    return {
      ...item,
      description: item.description?.trim() ? item.description : packDescription,
      language,
      presentationLanguage: String(item?.presentationLanguage ?? "").trim() ? item.presentationLanguage : language,
    };
  });
};

export const XevmpdPackage: React.FC<XevmpdPackageProps> = ({
  open,
  onClose,
  regdata,
  sideMenuData,
  eleigibleRegrecords
}) => {
  const [packLevel, setPackLevel] = useState<"yes" | "no">("no");
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  const [pageSize, setPageSize] = useState<number>(10);
  const [data, setData] = useState<any[]>([]);
  const [PackageData, setPackageData] = useState<any[]>([]);
  const navigator = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [recordAlreadyexist, setrecordAlreadyexist] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { allXevmpdRows } = useContext(XevmpdContext);
  const [initiateButtonDisabled, setInitiateButtonDisabled] = useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();
  // UAT #49: packs whose (pack, language) already has an xEVMPD record, listed below the grid
  const coveredPacks = useMemo(
    () => PackageData.filter((row: any) => row.disabled && row.existingRecordID),
    [PackageData]
  );
  // UAT #31: dialog rows with the Full Name of their language; PackageData/selection keep using `data`
  const packRowsWithNames = useMemo(
    () => attachFullNames(PackageData, sideMenuData?.["Presentation Details"]),
    [PackageData, sideMenuData]
  );
  const resetState = () => {
    setPackLevel("no");
    setSelectionModel([]);
    setPageSize(10);
    setData([]);
    setPackageData([]);
    setLoading(false);
    setrecordAlreadyexist(false);
  };
useEffect(() => {
    packLevel === "no"?setInitiateButtonDisabled(false):setInitiateButtonDisabled(true);
  }, [packLevel]);
  useEffect(() => {
    if (!open) {
      resetState();
      setError(false);
    }
  }, [open, onClose]);

  useEffect(() => {
    const getAllMissingPackageIds = (data: any[]) => {
      if (!data?.length) return [];

      const recordId = regdata[0]?.recordID || regdata[0]?.recordId;
      return data
        .filter(item => item.regId === recordId)
        .flatMap(item => item.missingPackageIds);
    };
    const getData = async () => {
      const packdata = await fetchRegistrationPacks(regdata[0]?.id, registrationMajorVersionOf(regdata[0]));
      const dynamicData = packdata?.data?.data?.[0]?.dynamicData ?? [];
      // UAT #9: one row per (pack, language); grey out selections that already have a live xEVMPD record
      let existing: any[] = [];
      try {
        const found = await fetchExistingPackSelections(regdata[0]?.recordID ?? regdata[0]?.recordId ?? "");
        existing = Array.isArray(found) ? found : [];
      } catch (e) {
        console.error("Failed to load existing xEVMPD pack selections:", e);
      }
      const packRows = flagExistingPackSelections(expandPackPresentationRows(dynamicData), existing);
      setrecordAlreadyexist(existing.length > 0);
      setPackageData(packRows);
      setData(packRows);
      // const result = await fetchRelatedProducts(regdata[0]?.productId);
      // if (result) {
      //   const packages = regdata[0]?.sectionTree?.Packages;
      //   if (packages?.Packages?.length > 0) {
      //     const RegLinkedPackages = result.data.filter((item: { id: any }) =>
      //       packages?.Packages?.includes(item.id)
      //     );
      //     setData(RegLinkedPackages);
      //     let missingpackages = getAllMissingPackageIds(eleigibleRegrecords);
      //     const getStatusByRegId = (data: any[], regId: string) => {
      //       return data.find(item => item.regId === regId)?.linked_through || "REGID_NOT_FOUND";
      //     };
      //     const statusofregrecord = getStatusByRegId(eleigibleRegrecords, regdata[0]?.recordID);
      //     if (statusofregrecord === "LINKED_THROUGH_REGISTRATION") {
      //       setrecordAlreadyexist(true);
      //       setPackageData(RegLinkedPackages);
      //     } else {
      //       if (statusofregrecord === "LINKED_THROUGH_PACKAGE") {
      //         let matchFound = false;
      //         const missingSet = new Set(missingpackages || []);
      //         const updatedPackRows = RegLinkedPackages.map((item: { id: any }) => {
      //           const isMatched = missingSet.has(item.id);
      //           if (isMatched) {
      //             matchFound = true;
      //           }
      //           return {
      //             ...item,
      //             disabled: missingSet.size > 0 ? !isMatched : false,
      //           };
      //         });
      //         matchFound ? setrecordAlreadyexist(true) : setrecordAlreadyexist(false);
      //         setPackageData(updatedPackRows);
      //       }
      //       else {
      //         setPackageData(RegLinkedPackages);
      //       }
      //     }
      //   } else {
      //     setData([]);
      //   }
      // }
    };

    getData();
  }, [open]);

  // useEffect(() => {
  //   let packagedata: any[] = [];
  //   const getallxevmpddata = async () => {
  //     //const results = await fetchXevmpdList({ page: 0, pageSize: 100000 }, { showActive: true, showDeleted: true, showSubmitted: true }, []);
  //     let reglevelxevmpdcreated = false;
  //     const xevmpddata = allXevmpdRows;
  //     const filtered = xevmpddata.filter(
  //       (entry: { xevmpdStatus: number; XEVMPDData: string; id: any;Regdata:any }) => {
  //         try {
  //          // const parsed = entry.XEVMPDData;
  //           const reg = entry.Regdata;
  //           const regid = regdata[0]?.recordID?.replace("REG-", "");
  //           if (
  //             reg?.id === regid &&
  //             (entry.xevmpdStatus === 0 || entry.xevmpdStatus === 2)
  //           ) {
  //             return true;
  //           }
  //         } catch (error) {
  //           console.error("Invalid JSON in xevmpdData:", entry.id);
  //           return false;
  //         }
  //       }
  //     );
  //     if (filtered.length === 0) {
  //       setPackageData(data);
  //       setrecordAlreadyexist(false);
  //     } else {
  //       let alreadyexist = false;
  //       for (const xevmpdrecord of filtered) {
  //         const xevmpdataobject = JSON.parse(xevmpdrecord.xevmpdData);
  //         const packagedatasaved = xevmpdataobject.PackageData;
  //         if (xevmpdataobject?.packLevel === false) {
  //           reglevelxevmpdcreated = true;
  //         }
  //         if (xevmpdataobject?.packLevel !== true) {
  //           alreadyexist = true;
  //         }
  //         if (packagedatasaved?.length === 0) {
  //           break;
  //         }
  //         for (const packdata of data) {
  //           if (packdata.id === packagedatasaved.id) {
  //             packdata.disabled = true;
  //             packagedata.push(packdata);
  //             alreadyexist = true;
  //           } else {
  //             packagedata.push(packdata);
  //           }
  //         }
  //       }
  //       const uniquePackages = Array.from(
  //         new Map(packagedata.map((item) => [item.id, item])).values()
  //       );
  //       alreadyexist
  //         ? setrecordAlreadyexist(true)
  //         : setrecordAlreadyexist(false);
  //       if (reglevelxevmpdcreated) {
  //         setrecordAlreadyexist(true);
  //       }

  //       setPackageData(uniquePackages);
  //     }
  //   };
  //   const getAllMissingPackageIds = (data: any[]) => {
  //     if (!data?.length) return [];

  //     const recordId = regdata[0]?.recordID || regdata[0]?.recordId;
  //     return data
  //       .filter(item => item.regId === recordId)
  //       .flatMap(item => item.missingPackageIds);
  //   };
  //   let missingpackages = getAllMissingPackageIds(eleigibleRegrecords);
  //   //getallxevmpddata();
  // }, [data]);

  const generateRegionRecords = (basePayload: any) => {
    const regionSet = [
      { country: basePayload.xevmpdData.Regdata.country },
      { country: "Norway" },
      { country: "Iceland" },
      { country: "Liechtenstein" },
    ];

    return regionSet.map(r => {
      const cloned = JSON.parse(JSON.stringify(basePayload));

      // Set correct country for the record
      cloned.xevmpdData.Regdata.country = r.country;

      cloned.xevmpdData.MenuData.Authorisation.authorizationCountryCode = r.country;

      return cloned;
    });
  };


  const savexEVMPDData = async () => {
    setLoading(true);
    const selectedRows = data.filter((row) =>
      selectionModel.includes((row as any).rowId ?? (row as any).id)
    );
    if (selectedRows.length === 0 && packLevel === "yes") {
      setError(true);
      setLoading(false);
      return;
    } else {
      setError(false);
    }
    console.log("Package Selected", selectedRows);
    console.log("Registration record selected:", regdata);
    const Regid = regdata[0].recordID.replace("REG-", "");
    const RegidforaddionAPI = regdata[0].id;
    const Registrationobject = regdata[0];
    const procType = Registrationobject.procedureType?.trim();
    const isCP = ["Centralised Procedure", "Centralized Procedure"].includes(procType);

    let xevmpdrecordcreated = false;
    if (selectedRows.length > 0 && packLevel === "yes") {
      for (const selectedRow of selectedRows) {
        // rowId / existingRecordID are dialog-only; PackageData keeps the real Pack Presentation id + one language.
        // UAT #40: the pack's own Full Name is stored as it is; a blank one is left out.
        const { rowId, existingRecordID, ...packagedata } = selectedRow as any;
        if (!String(packagedata.fullName ?? "").trim()) delete packagedata.fullName;
        const XevMPDData = [
          {
            registrationId: RegidforaddionAPI,
            xevmpdData: {
              Regdata: {
                ...Registrationobject,
                authorizationNumber:
                  sideMenuData.Authorisation.authorizationNumber,
                packLevel: true,
              },
              PackageData: { ...packagedata, disabled: true },
              packLevel: true,
              MenuData: {
                ...sideMenuData,
                // UAT #9: at creation the BE cannot see PackageData yet, so reduce the Pack Presentation rows
                // (one per pack x language) to the selected pack and stamp its description/language here.
                "Presentation Details": selectPresentationDetailsForPack(
                  sideMenuData["Presentation Details"],
                  packagedata
                ),
                Authorisation: {
                  ...sideMenuData.Authorisation,
                  "mRP/DCP/EMEAnumber":
                    sideMenuData.Authorisation.procedureNumber ?? "",
                  authorizationCountryCode: Registrationobject.country,
                },
              },
              xevmpdFormData: { version: 0.1, xevmpdStatus: "Draft" },
            },
            createdBy: Cookies.get("USER_EMAIL"),
          },
        ];
        console.log("Updated Code");
        let payloadToSend = XevMPDData;
        // Create 4 CP/EEA records only for Centralised Procedure
        if (isCP) {
          payloadToSend = generateRegionRecords(XevMPDData[0]);
        }
        const response = await createXevmpd(payloadToSend);
        if (response?.data.status === 200) {
          xevmpdrecordcreated = true;
        }
      }
    } else {
      const basePayload = {
          registrationId: RegidforaddionAPI,
          xevmpdData: {
            Regdata: {
              ...Registrationobject,
              authorizationNumber:
                sideMenuData.Authorisation.authorizationNumber,
              packLevel: false,
            },
            PackageData: {},
            packLevel: false,
            MenuData: {
              ...sideMenuData,
              Authorisation: {
                ...sideMenuData.Authorisation,
                "mRP/DCP/EMEAnumber":
                  sideMenuData.Authorisation.procedureNumber ?? "",
                authorizationCountryCode: Registrationobject.country,
              },
            },
            xevmpdFormData: { version: 0.1, xevmpdStatus: "Draft" },
          },
          createdBy: Cookies.get("USER_EMAIL"),
      };
      let payloadToSend = [basePayload];
      if (isCP) {
        payloadToSend = generateRegionRecords(basePayload);
      }
      console.log("Payload sent to backend:", payloadToSend);
      const response = await createXevmpd(payloadToSend);
      if (response?.data.status === 200) {
        xevmpdrecordcreated = true;
      }
    }
    if (xevmpdrecordcreated) {
      enqueueSnackbar("xEVMPD(s) created successfully", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    }
    setLoading(false);
    navigator("/idmp-list", { replace: true });
  };

  useEffect(() => {
    if (recordAlreadyexist) {
      setPackLevel("yes");
    } else {
      setPackLevel("no");
    }
  }, [recordAlreadyexist]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: "800px",
          maxWidth: "90%",
          borderRadius: "0px",
          "& .MuiDialogContent-root p": {
            fontSize: "12px !important",
            color: "#0d0d0d",
          },
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 15, 15, 0.4) !important",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontSize={20} fontWeight={500}>
          Initiate xEVMPD ({regdata[0]?.recordID})
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" fontSize={12} sx={{ mb: 1 }}>
          Do you want to submit xEVMPD at the pack level?
        </Typography>

        <RadioGroup
          row
          value={packLevel}
         // onChange={(e) => setPackLevel(e.target.value as "yes" | "no")}
          onChange={(e) => {
            setPackLevel(e.target.value as "yes" | "no");
            e.target.value === "no"?setInitiateButtonDisabled(false):setInitiateButtonDisabled(true);
            if(e.target.value === "no"){
              setSelectionModel([]);
            }
          }}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          {recordAlreadyexist ? (
            <Tooltip title="Registration-level xEVMPD cannot be initiated as one or more packages are already part of a pack-level xEVMPD record">
              <span>
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label="No"
                  disabled
                />
              </span>
            </Tooltip>
          ) : (
            <FormControlLabel value="no" control={<Radio />} label="No" />
          )}
        </RadioGroup>

        {packLevel === "yes" && (
          <>
            <Typography variant="body1" sx={{ mt: 2, mb: 1, fontSize: "12px" }}>
              Select the packs to Initiate xEVMPD{" "}
              <span style={{ color: "red" }}>*</span>
            </Typography>

            <Box sx={{ width: "100%" }}>
              {/* DataGrid Box */}
              <Box sx={{ height: 300 }}>
                <DataGridPremium
  className="add-wizard-grid" /* UAT #34 reopened: always-visible horizontal scroll bar */
  rows={packRowsWithNames}
  getRowId={(row) => row.rowId ?? row.id}
  columns={packColumns}
  disableVirtualization /* UAT #31: six columns, at most 20 rows per page — always render every column */
  checkboxSelection
  rowSelectionModel={selectionModel}
  onRowSelectionModelChange={(newSelection) => {
  setSelectionModel(newSelection);
  newSelection.length > 0? setInitiateButtonDisabled(false): setInitiateButtonDisabled(true);
}}
  slots={{
    noRowsOverlay: NoRecordsFound,
    noResultsOverlay: NoRecordsFound,
  }}
  pagination
  pageSizeOptions={[5, 10, 20]}
  onPaginationModelChange={(model) => {
    setPageSize(model.pageSize);
  }}
  initialState={{
    pagination: {
      paginationModel: { pageSize: 5 },
    },
    pinnedColumns: { right: [] },
  }}
  isRowSelectable={(params) => !params.row.disabled}
  sx={{
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: "bold",
    },
    "& .MuiDataGrid-columnHeaders": {
      fontSize: 13,
      fontWeight: 500,
      backgroundColor: "#f8fafdff",
    },
    "& .MuiDataGrid-cell": {
      fontSize: 13,
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
    },
    // Fix for overlay positioning and centering
    "& .MuiDataGrid-overlay": {
      height: "100% !important",
      width: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
      position: "absolute !important",
      top: "0 !important",
      left: "0 !important",
      right: "0 !important",
      bottom: "0 !important",
      backgroundColor: "white !important",
    },
    "& .MuiDataGrid-overlayWrapper": {
      height: "100% !important",
      position: "relative !important",
    },
    // Style the image inside
    "& .MuiDataGrid-overlay img": {
      width: "80px !important",
      height: "80px !important",
    },
    
  }}
/>
              </Box>

              {/* UAT #9: make the greyed-out selections explicit; UAT #49: one pack per line, scrolls when many */}
              {coveredPacks.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography id="covered-packs-title" variant="body2" sx={{ fontSize: "12px", color: "#5f6368" }}>
                    Already covered by an xEVMPD record ({coveredPacks.length}):
                  </Typography>
                  <Box
                    component="ul"
                    aria-labelledby="covered-packs-title"
                    sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: "96px", overflowY: "auto", fontSize: "12px", color: "#5f6368" }}
                  >
                    {coveredPacks.map((row: any) => (
                      <li key={row.rowId ?? `${row.id}__${row.presentationLanguage}`}>
                        {`${row.packageName}${row.presentationLanguage ? ` (${row.presentationLanguage})` : ""} → ${row.existingRecordID}`}
                      </li>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Error Alert BELOW grid */}
              {error && (
                <Typography
                  variant="body2"
                  sx={{ color: "red !important", mt: 1 }}
                >
                  Please select at least one pack to initiate XEVMPD
                </Typography>
              )}
            </Box>
          </>
        )}

        <Box
          display="flex"
          alignItems="center"
          bgcolor="#e8efff"
          borderRadius={1}
          p={1.5}
        >
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              bgcolor: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 12,
              fontWeight: "bold",
              mr: 1.5,
            }}
          >
            i
          </Box>
          <Typography sx={{ fontSize: 13 }}>
            Make sure all required xEVMPD fields have been filled before
            initiating.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          disableElevation
          sx={{
            backgroundColor: "#f4f1f1ff !important", // light grey
            color: "black !important",
            borderRadius: "6px",
            textTransform: "none", // preserve 'Cancel' casing
            boxShadow: "none",
            fontWeight: 400,
            fontSize: "0.875rem",
            minWidth: "64px",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "#e0e0e0 !important",
              boxShadow: "none",
            },
          }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button variant="contained" onClick={savexEVMPDData} disabled = {initiateButtonDisabled}>
          Initiate xEVMPD
        </Button>
      </DialogActions>
      <Loading open={loading} />
    </Dialog>
  );
};
